import { auth } from '../platform/firebase/clients';
import { logError, LogErrorOptions, LogSeverity } from '../services/logger';
import { getStorageDownloadDiagnostics } from '../services/storageDownloadDiagnostics';
import { categorizeError } from './errorCategory';

/**
 * Centralized error logging used by the global error handlers, the React
 * error boundary, and any callsite that wants explicit `context` /
 * `metadata` attached before the entry hits Cloud Logging.
 *
 * Mirrors aligned's GlobalErrorLogger surface so engineers familiar with
 * that codebase find the same shape here. Two extras over the basic
 * `logError`:
 *
 *   1. Auto-attaches the current Firebase user (uid + email) when the
 *      caller doesn't pass one. Saves every callsite from re-doing the
 *      `auth.currentUser` dance and the null-guarding around test envs.
 *   2. Debounces identical error messages within a 2 s window, so a render
 *      loop or onError storm can't flood Error Reporting (which charges per
 *      ingested event).
 */

const DEBOUNCE_MS = 2000;
const recentErrorAt = new Map<string, number>();

function shouldDebounce(messageKey: string): boolean {
  const now = Date.now();
  const last = recentErrorAt.get(messageKey);
  if (last !== undefined && now - last < DEBOUNCE_MS) {
    return true;
  }
  recentErrorAt.set(messageKey, now);
  // Trim the map periodically so it can't grow without bound when distinct
  // errors fire over a long session.
  if (recentErrorAt.size > 200) {
    const cutoff = now - DEBOUNCE_MS;
    recentErrorAt.forEach((at, key) => {
      if (at < cutoff) recentErrorAt.delete(key);
    });
  }
  return false;
}

function currentAuthUser(): { uid?: string; email?: string | null } | undefined {
  try {
    const u = auth.currentUser;
    if (!u) return undefined;
    return { uid: u.uid, email: u.email };
  } catch {
    return undefined;
  }
}

function debounceKey(error: unknown, contextLabel?: string): string {
  const msg =
    error instanceof Error
      ? `${error.name}:${error.message}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return String(error);
            } catch {
              return 'unknown';
            }
          })();
  return contextLabel ? `${contextLabel}::${msg}` : msg;
}

export interface GlobalLogOptions {
  /** Short label naming the originating site (e.g. "FlutterError" / "PlatformDispatcher" / "TaskQueue.subscribe"). */
  context?: string;
  /** Arbitrary structured fields attached as `jsonPayload.frontendContext.*`. */
  metadata?: Record<string, unknown>;
  /** Force a severity (skips auth/network downgrade). */
  severity?: LogSeverity;
}

/**
 * Log an error with optional structured context. Auth/network errors are
 * downgraded to WARNING automatically (they're expected on logout /
 * connectivity loss). Pass `severity: 'ERROR'` to bypass.
 */
export function logGlobalError(
  error: unknown,
  options: GlobalLogOptions = {},
): void {
  const key = debounceKey(error, options.context);
  if (shouldDebounce(key)) return;

  const category = categorizeError(error);
  const storageDiagnostics = getStorageDownloadDiagnostics(error);
  const metadata: Record<string, unknown> = {
    ...(options.metadata || {}),
    ...(storageDiagnostics || {}),
    errorCategory: category.type,
  };

  const payload: LogErrorOptions = {
    context: options.context,
    metadata,
    user: currentAuthUser(),
  };
  if (options.severity) payload.severity = options.severity;
  logError(error, payload);
}

/**
 * Log an error from a Firestore / async stream onError handler. Same
 * downgrade rules as `logGlobalError`; included as a separate export so
 * callsites read clearly at a glance.
 */
export function logStreamError(
  error: unknown,
  options: GlobalLogOptions = {},
): void {
  logGlobalError(error, options);
}

export const _internal = { shouldDebounce, debounceKey, recentErrorAt };
